import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ImportJobView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  FileField,
  formatFileSize,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  cancelImport,
  commitImport,
  importIssuesQuery,
  importQuery,
  importReportUrl,
  importsQuery,
  sha256Hex,
} from '../api/terminology.ts';
import { useOnline } from '../hooks/use-online.ts';
import { uploadFile, type UploadProgress } from '../imports/upload.ts';
import styles from '../patients/patients.module.css';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { importStatusTag } from './settings-imports.tsx';
import { SettingsPage } from './settings-nav.tsx';

// One import (plan §6.3): progress in words, the preview of problems, the report, and
// the import itself. While Wodi is checking or importing, the page refreshes itself.

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const n = (value: number) => value.toLocaleString('en-KE');

/** Counts in words; kinds' count keys are camelCase codes. Unknown keys are shown as they are. */
const COUNT_WORDS: Record<string, readonly [string, string]> = {
  newPrices: ['new price', 'new prices'],
  changedPrices: ['price change', 'price changes'],
  unchanged: ['price unchanged', 'prices unchanged'],
  free: ['free item', 'free items'],
  newServices: ['new service', 'new services'],
  documents: ['opening balance document', 'opening balance documents'],
  lines: ['line', 'lines'],
};

/**
 * Counts in a fixed order. They are stored as jsonb, which doesn't keep key order, so the
 * order comes from here: known outcomes first, as listed above, then any others.
 */
function ordered(counts: Record<string, number>): [string, number][] {
  const known = Object.keys(COUNT_WORDS);
  const rank = (key: string) => (known.includes(key) ? known.indexOf(key) : known.length);
  return Object.entries(counts).sort(([a], [b]) => rank(a) - rank(b));
}

export function countInWords(what: string, count: number): string {
  const words = COUNT_WORDS[what];
  if (words === undefined) return `${n(count)} ${what}`;
  return `${n(count)} ${count === 1 ? words[0] : words[1]}`;
}

/**
 * What the commit added, in words: "Import finished. 2 concepts, 3 designations added."
 * Confirmed possible duplicates are a note about patients already counted, not an addition.
 */
export function finishedMessage(counts: Record<string, number>): string {
  const confirmed = counts['possibleDuplicatesConfirmed'] ?? 0;
  const parts = ordered(counts)
    .filter(([what, count]) => what !== 'possibleDuplicatesConfirmed' && count > 0)
    .map(([what, count]) => countInWords(what, count));
  const added = parts.length === 0 ? 'Nothing new was' : parts.join(', ');
  const note =
    confirmed === 0
      ? ''
      : confirmed === 1
        ? ' 1 of them was a possible duplicate, confirmed as a different person.'
        : ` ${n(confirmed)} of them were possible duplicates, confirmed as different people.`;
  return `Import finished. ${added} added.${note}`;
}

/** The preview's counts in words, leaving out zeros: "3 new prices, 1 price change". */
export function previewInWords(counts: Record<string, number>): string {
  const parts = ordered(counts)
    .filter(([, count]) => count > 0)
    .map(([what, count]) => countInWords(what, count));
  return parts.length === 0 ? 'nothing new' : parts.join(', ');
}

function Summary({ job }: { job: ImportJobView }) {
  const importable = job.validRows + job.warningRows;
  switch (job.status) {
    case 'uploading':
      return (
        <p>{`Uploading: ${String(job.receivedChunks.length)} of ${String(job.chunkCount)} parts received.`}</p>
      );
    case 'uploaded':
    case 'validating':
      return <p role="status">{`Checking rows: ${n(job.totalRows)} checked so far.`}</p>;
    case 'validated':
      return (
        <>
          <p>
            {job.invalidRows === 0
              ? `${n(importable)} rows ready to import${job.warningRows > 0 ? `, ${n(job.warningRows)} of them with notes` : ''}.`
              : `${n(importable)} rows ready, ${n(job.invalidRows)} rows with problems. Fix the problems in the file and upload it again; nothing has been imported.`}
          </p>
          {job.invalidRows === 0 && Object.keys(job.previewCounts).length > 0 ? (
            <p>{`What importing will do: ${previewInWords(job.previewCounts)}.`}</p>
          ) : null}
        </>
      );
    case 'committing':
      return (
        <p role="status">{`Importing: ${n(job.committedThroughRow)} of ${n(job.totalRows)} rows.`}</p>
      );
    case 'committed':
      return (
        <>
          <p>{finishedMessage(job.resultCounts)}</p>
          {job.kind === 'opening_stock' ? (
            <Notice tone="info">
              No stock has moved yet. Someone other than you with approval rights approves each
              document under the store's adjustments on the Stores page.
            </Notice>
          ) : null}
        </>
      );
    case 'failed':
      return null;
    case 'cancelled':
      return <p>This import was cancelled. Nothing was imported.</p>;
  }
}

function ResumeUpload({ job }: { job: ImportJobView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const resume = useMutation({
    mutationFn: async (chosen: File) => {
      if (chosen.size !== job.fileSizeBytes || (await sha256Hex(chosen)) !== job.fileSha256) {
        throw new Error('different file');
      }
      return uploadFile(job, chosen, setProgress);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['imports'] });
    },
  });
  return (
    <div className={styles.inlineForm}>
      <Notice tone="info">
        The upload stopped before the whole file arrived. Choose the same file to carry on where it
        stopped.
      </Notice>
      <FileField
        label="File"
        file={file}
        onChange={setFile}
        acceptedFileTypes={['.csv', 'text/csv']}
        {...(resume.error === null
          ? {}
          : {
              errorMessage:
                resume.error instanceof ApiError
                  ? resume.error.message
                  : 'That is not the same file. Choose the file this import started with.',
            })}
      />
      {progress === null || !resume.isPending ? null : (
        <p role="status">
          {`Uploading, ${formatFileSize(progress.sentBytes)} of ${formatFileSize(progress.totalBytes)}`}
        </p>
      )}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={!online || file === null}
          isPending={resume.isPending}
          pendingLabel="Uploading…"
          onPress={() => {
            if (file !== null) resume.mutate(file);
          }}
        >
          Carry on uploading
        </Button>
      </div>
    </div>
  );
}

function Issues({ job }: { job: ImportJobView }) {
  const issues = useQuery({
    ...importIssuesQuery(job.id),
    enabled: job.status !== 'uploading',
  });
  const hasIssues =
    job.issueGroups.length > 0 || job.headerIssues.length > 0 || job.errorCode !== null;
  if (!hasIssues) return null;
  return (
    <section aria-label="Problems">
      <h2>Problems</h2>
      {job.errorCode === null ? null : (
        <Notice tone="error">
          {job.errorLine === null
            ? job.errorMessage
            : `Line ${String(job.errorLine)}: ${job.errorMessage ?? ''}`}
        </Notice>
      )}
      {job.headerIssues.length === 0 ? null : (
        <ul>
          {job.headerIssues.map((h) => (
            <li key={`${h.code}:${h.column}:${String(h.position)}`}>
              {h.column === '' ? h.message : `${h.column}: ${h.message}`}
            </li>
          ))}
        </ul>
      )}
      {job.issueGroups.length === 0 ? null : (
        <Table aria-label="Problems by kind">
          <TableHeader>
            <Column isRowHeader>Problem</Column>
            <Column>Rows</Column>
            <Column>First lines</Column>
          </TableHeader>
          <TableBody items={job.issueGroups.map((g) => ({ ...g, id: `${g.severity}:${g.code}` }))}>
            {(g) => (
              <Row id={g.id}>
                <Cell>{g.severity === 'warning' ? `Note: ${g.message}` : g.message}</Cell>
                <Cell>{n(g.count)}</Cell>
                <Cell>{g.examples.map((e) => String(e.line)).join(', ')}</Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      )}
      {issues.isSuccess && issues.data.items.length > 0 ? (
        <Table aria-label="Rows with problems">
          <TableHeader>
            <Column isRowHeader>Line</Column>
            <Column>Row</Column>
            <Column>Column</Column>
            <Column>Problem</Column>
          </TableHeader>
          <TableBody
            items={issues.data.items.map((i, index) => ({
              ...i,
              id: `${String(i.rowNumber)}:${String(index)}`,
            }))}
          >
            {(i) => (
              <Row id={i.id}>
                <Cell>{String(i.line)}</Cell>
                <Cell>{String(i.rowNumber)}</Cell>
                <Cell>{i.column ?? ''}</Cell>
                <Cell>{i.message}</Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      <p>
        <a href={importReportUrl(job.id)} download>
          Download all problems (CSV)
        </a>
      </p>
    </section>
  );
}

export function ImportDetailPage({ importId }: { importId: string }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const job = useQuery({
    ...importQuery(importId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'uploaded' || status === 'validating' || status === 'committing'
        ? 2000
        : false;
    },
  });
  const [cancelling, setCancelling] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['imports'] });
  };
  const commit = useMutation({
    mutationFn: ({ expected, reason }: { expected: number; reason: string | null }) =>
      commitImport(importId, expected, reason),
    onSuccess: async () => {
      setAcknowledging(false);
      await refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: (reason: string) => cancelImport(importId, reason),
    onSuccess: async () => {
      await refresh();
      await queryClient.invalidateQueries({ queryKey: importsQuery.queryKey });
      toasts.show({ title: 'Import cancelled' });
      setCancelling(false);
    },
  });

  if (job.isPending) return <LoadingBlock label="Loading the import" rows={4} />;
  if (job.isError) {
    return (
      <>
        <PageHeader title="Import" />
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void job.refetch()}>
              Try again
            </Button>
          }
        >
          {message(job.error, "The import couldn't be loaded. Try again.")}
        </Notice>
      </>
    );
  }

  const data = job.data;
  const importable = data.validRows + data.warningRows;
  const canCancel = ['uploading', 'uploaded', 'validating', 'validated'].includes(data.status);

  return (
    <SettingsPage title={data.fileName ?? 'Import'}>
      <p>
        {importStatusTag(data.status)}{' '}
        <span className="wodi-id">{data.fileSha256.slice(0, 12)}</span>
      </p>
      <Summary job={data} />
      {online ? null : (
        <Notice tone="info">
          No connection to Wodi. This page will update when you are back online.
        </Notice>
      )}
      {data.status === 'uploading' ? <ResumeUpload job={data} /> : null}
      <Issues job={data} />
      {commit.error === null || acknowledging ? null : (
        <Notice tone="error">{message(commit.error, "The import didn't start. Try again.")}</Notice>
      )}
      {data.status === 'validated' && data.invalidRows === 0 && data.acknowledgementRequired ? (
        <Notice tone="info">
          Some rows may be people already registered, or the same person twice in the file. Check
          them in the problems above. To import them as different people, you will be asked why.
        </Notice>
      ) : null}
      <div className={styles.actions}>
        {data.status === 'validated' ? (
          <Button
            variant="primary"
            isDisabled={!online || data.invalidRows > 0}
            isPending={commit.isPending}
            pendingLabel="Starting…"
            onPress={() => {
              if (data.acknowledgementRequired) setAcknowledging(true);
              else commit.mutate({ expected: importable, reason: null });
            }}
          >
            {`Import ${n(importable)} rows`}
          </Button>
        ) : null}
        {canCancel ? (
          <Button
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              setCancelling(true);
            }}
          >
            Cancel import
          </Button>
        ) : null}
      </div>
      {data.status === 'validated' && data.invalidRows > 0 ? (
        <p>Import is not available while any row has a problem.</p>
      ) : null}
      <ReasonDialog
        title="Import possible duplicates?"
        isOpen={acknowledging}
        onOpenChange={setAcknowledging}
        confirmLabel={`Import ${n(importable)} rows`}
        pendingLabel="Starting…"
        isPending={commit.isPending}
        error={
          commit.error === null
            ? null
            : message(commit.error, "The import didn't start. Try again.")
        }
        onConfirm={(reason) => {
          commit.mutate({ expected: importable, reason });
        }}
      >
        <p>
          The rows marked as possible duplicates will be registered as new patients. Say why you are
          sure they are different people. The reason is kept with each patient's record.
        </p>
      </ReasonDialog>
      <ReasonDialog
        title="Cancel this import?"
        isOpen={cancelling}
        onOpenChange={setCancelling}
        confirmLabel="Cancel import"
        pendingLabel="Cancelling…"
        isPending={cancel.isPending}
        error={
          cancel.error === null ? null : message(cancel.error, "It wasn't cancelled. Try again.")
        }
        onConfirm={(reason) => {
          cancel.mutate(reason);
        }}
      >
        <p>Nothing from this file will be imported. The record of the import is kept.</p>
      </ReasonDialog>
    </SettingsPage>
  );
}
